import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Download, ArrowRight, AlertCircle, Video, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Handles the post-Stripe redirect for BOTH photo and video purchases.
 * Tries the photo purchase endpoint first; if the session isn't a photo
 * purchase, falls back to the video purchase endpoint.
 */
const PurchaseSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState(null); // 'photo' | 'video'
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloadedAll, setDownloadedAll] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError('Missing session ID');
      setLoading(false);
      return;
    }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        // Try photos first, then videos
        let res;
        let resolvedKind = 'photo';
        try {
          res = await axios.get(`${API_URL}/api/photos/purchase/${sessionId}`);
        } catch (photoErr) {
          if (photoErr.response?.status === 404) {
            res = await axios.get(`${API_URL}/api/videos/purchase/${sessionId}`);
            resolvedKind = 'video';
          } else {
            throw photoErr;
          }
        }

        if (res.data.status === 'paid') {
          setKind(resolvedKind);
          setData(res.data);
          setLoading(false);
          sessionStorage.removeItem('photo_cart');
          sessionStorage.removeItem('video_cart');
          return;
        }
        if (attempts < 10) {
          setTimeout(poll, 1500);
        } else {
          setError('Payment is still processing. Please refresh in a moment.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Purchase fetch error:', err);
        setError(err.response?.data?.detail || 'Could not verify purchase');
        setLoading(false);
      }
    };
    poll();
  }, [sessionId]);

  const items = data ? (kind === 'video' ? data.videos : data.photos) : [];
  const idKey = kind === 'video' ? 'video_id' : 'photo_id';
  const label = kind === 'video' ? 'video' : 'photo';

  const downloadOne = async (item) => {
    try {
      const res = await fetch(`${API_URL}${item.download_url}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename || (kind === 'video' ? 'video.mp4' : 'photo.jpg');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error(`Could not download ${item.filename}`);
    }
  };

  const downloadAll = async () => {
    for (const item of items) {
      await downloadOne(item);
      await new Promise(r => setTimeout(r, 400));
    }
    setDownloadedAll(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Verifying your purchase…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl uppercase mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/gallery"><Button>Back to Gallery</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Check className="w-12 h-12 text-white" strokeWidth={3} />
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight">Purchase Complete</h1>
          <p className="text-muted-foreground mt-3 text-lg">
            {items.length} {label}{items.length !== 1 ? 's' : ''} ready to download · ${data.total.toFixed(2)} CAD
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Receipt sent to <span className="font-medium">{data.customer_email}</span>
          </p>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-900">
            <p className="font-bold">Download now — bookmark this page if you need to come back.</p>
            <p>Your download link is tied to this session. We recommend downloading immediately.</p>
          </div>
        </div>

        <div className="text-center mb-8">
          <Button
            size="lg"
            onClick={downloadAll}
            className="rounded-full px-8 font-bold uppercase tracking-wider"
            data-testid="download-all-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            {downloadedAll ? 'Download Again' : `Download All (${items.length})`}
          </Button>
          {kind === 'video' && (
            <p className="text-xs text-muted-foreground mt-2 italic">
              Videos can be large — downloads may take a few minutes each.
            </p>
          )}
        </div>

        <div className={`grid gap-4 ${kind === 'video' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}>
          {items.map(item => (
            <div key={item[idKey]} className="bg-card border border-border rounded-lg overflow-hidden">
              <div className={`bg-muted flex items-center justify-center ${kind === 'video' ? 'aspect-video' : 'aspect-square'}`}>
                {item.thumbnail_url ? (
                  <img src={item.thumbnail_url} alt={item.filename} className="w-full h-full object-cover" draggable={false} />
                ) : (
                  kind === 'video' ? <Video className="w-10 h-10 opacity-40" /> : <Camera className="w-10 h-10 opacity-40" />
                )}
              </div>
              <div className="p-3">
                <p className="text-xs text-muted-foreground truncate mb-2">{item.event_title}</p>
                <p className="text-xs font-mono truncate mb-2">{item.filename}</p>
                <Button
                  onClick={() => downloadOne(item)}
                  variant="outline"
                  size="sm"
                  className="w-full"
                  data-testid={`download-${label}-${item[idKey]}`}
                >
                  <Download className="w-3 h-3 mr-1" /> Download
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/gallery">
            <Button variant="outline" size="lg">
              Continue Browsing <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PurchaseSuccessPage;
